import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { ChartLine } from "@phosphor-icons/react";

const formatCurrency = (value) => {
  if (value >= 10000000) {
    return `₹${(value / 10000000).toFixed(1)}Cr`;
  } else if (value >= 100000) {
    return `₹${(value / 100000).toFixed(1)}L`;
  } else {
    return `₹${(value / 1000).toFixed(0)}K`;
  }
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white/95 backdrop-blur-xl border border-slate-200/50 shadow-lg rounded-sm p-4">
        <p className="font-mono text-sm font-semibold text-slate-900 mb-2">
          Year {payload[0].payload.year}
        </p>
        {payload.map((entry, index) => (
          <p key={index} className="font-mono text-sm text-slate-700">
            <span className="inline-block w-3 h-3 rounded-full mr-2" style={{ backgroundColor: entry.color }}></span>
            {entry.name}: {formatCurrency(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export const RevenueChart = ({ data }) => {
  return (
    <div className="bg-white rounded-sm border border-slate-200 p-6 lg:p-8" data-testid="revenue-chart">
      <div className="flex items-center gap-3 mb-6 pb-6 border-b border-slate-200">
        <ChartLine size={24} weight="duotone" className="text-emerald-700" />
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-slate-900">Revenue Projection</h2>
          <p className="text-sm text-slate-500 mt-0.5">Year-over-year AUM and trail revenue growth</p>
        </div>
      </div>

      <div className="w-full" style={{ height: '400px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorAUM" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#047857" stopOpacity={0.2}/>
                <stop offset="95%" stopColor="#047857" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#D97706" stopOpacity={0.2}/>
                <stop offset="95%" stopColor="#D97706" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis 
              dataKey="year" 
              stroke="#64748B"
              tick={{ fill: '#64748B', fontFamily: 'JetBrains Mono', fontSize: 12 }}
              tickLine={false}
              axisLine={{ stroke: '#E2E8F0' }}
            />
            <YAxis 
              stroke="#64748B"
              tick={{ fill: '#64748B', fontFamily: 'JetBrains Mono', fontSize: 12 }}
              tickFormatter={formatCurrency}
              tickLine={false}
              axisLine={{ stroke: '#E2E8F0' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ fontFamily: 'IBM Plex Sans', fontSize: '14px' }}
              iconType="circle"
            />
            <Area 
              type="monotone" 
              dataKey="ending_aum" 
              stroke="#047857" 
              strokeWidth={2}
              fillOpacity={1} 
              fill="url(#colorAUM)" 
              name="Ending AUM"
              animationDuration={800}
            />
            <Area 
              type="monotone" 
              dataKey="cumulative_revenue" 
              stroke="#D97706" 
              strokeWidth={2}
              fillOpacity={1} 
              fill="url(#colorRevenue)" 
              name="Cumulative Revenue"
              animationDuration={800}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default RevenueChart;